/**
 * Where the Happy Path PHASE COLUMNS belong after an auto-layout — as a pure decision.
 *
 * ## Why this exists
 *
 * KOA-15840 made phases first-class on the canvas: each one is a `bpmn:Group` column
 * that TILES the pool (no gaps, no overlap, full height), and a step's phase is derived
 * from WHICH COLUMN CONTAINS ITS CENTRE. Geometry is the authority, and the diagram
 * interceptor re-derives the assignment on every group/step move.
 *
 * KOA-18136 declares phases from the other end: `PHASE` / `PHASE_ASSIGN` write the model
 * (`happyPathDefinition` + each step's `happyPath`) and never touch the diagram. Both are
 * correct on their own, and together they disagree: an AI-declared assignment survives only
 * until the step's position says otherwise.
 *
 * Auto-layout is where the two can be reconciled honestly, because it is the moment the
 * step positions are (re)decided anyway. This module answers, for that moment:
 *
 *   - which X range each declared phase owns, given where layout put its steps, and
 *   - which steps landed outside their own phase's range and must be nudged in.
 *
 * After applying both, the geometry SAYS what the model DECLARED — so the interceptor's
 * next derivation agrees instead of overwriting, and the engine's business status (which
 * reads the phase) reports the phase the author actually asked for.
 *
 * ## What it deliberately does NOT do
 *
 * It does not re-order steps. Column boundaries are read OFF the laid-out positions, so
 * the flow layout ELK produced is preserved exactly; only steps that contradict their own
 * declaration move, and only horizontally. A process whose declared phase order disagrees
 * with its flow order therefore ends up with the flow intact and the outliers pulled into
 * their columns — visible, rather than silently re-assigned.
 */

/** A declared phase, in the order the author declared it. */
export interface IPhaseDeclaration {
  /** `IHappyPathPhaseModel.id` — the join key with the canvas group's `phaseId`. */
  id: string;
  /** Display name; used only for diagnostics. */
  name: string;
  /** `phaseOrder`; ties are broken by declaration index so the result is deterministic. */
  order: number;
}

/** One laid-out step, as the element registry reports it after layout. */
export interface IPhaseLayoutStep {
  id: string;
  /** The phase this step DECLARES (`extraParameters.happyPath`), or undefined when unassigned. */
  phaseId: string | undefined;
  x: number;
  width: number;
}

/** The pool's inner horizontal band the columns must tile exactly. */
export interface IPhaseLayoutPool {
  x: number;
  width: number;
}

/** The rectangle one phase column must occupy (horizontally; height is the pool's). */
export interface IPhaseColumnRect {
  phaseId: string;
  x: number;
  width: number;
}

/** A step that contradicted its own declaration and must move to `x`. */
export interface IPhaseStepNudge {
  stepId: string;
  x: number;
}

export interface IPhaseLayoutPlan {
  /** Columns left-to-right, tiling the pool exactly. Empty when there is nothing to tile. */
  columns: IPhaseColumnRect[];
  /** Steps whose laid-out X put them in the wrong column. Empty when layout already agreed. */
  nudges: IPhaseStepNudge[];
}

/** Breathing room between a column edge and a step inside it, so membership is never visually ambiguous. */
const COLUMN_PADDING: number = 12;
/** Floor for a column, wide enough that a default phase title never truncates (mirrors the interceptor). */
const MIN_COLUMN_WIDTH: number = 120;

/**
 * Plan the phase columns for a freshly laid-out diagram.
 *
 * Returns an EMPTY plan (no columns, no nudges) when there is nothing to decide — no
 * phases, or a pool too narrow to carry one column per phase. An empty plan means "leave
 * the canvas alone", never "erase the columns": auto-layout must not be able to destroy
 * phases just because it could not place them.
 */
export function planPhaseColumns(
  phases: readonly IPhaseDeclaration[],
  steps: readonly IPhaseLayoutStep[],
  pool: IPhaseLayoutPool
): IPhaseLayoutPlan {
  const empty: IPhaseLayoutPlan = { columns: [], nudges: [] };
  if (phases.length === 0) return empty;
  if (pool.width < MIN_COLUMN_WIDTH * phases.length) return empty;

  // Declaration order is the column order. `phaseOrder` first, then the declared index,
  // so two phases sharing an order never swap between runs.
  const ordered: IPhaseDeclaration[] = phases
    .map((phase: IPhaseDeclaration, index: number): { phase: IPhaseDeclaration; index: number } => ({ phase, index }))
    .sort(
      (a: { phase: IPhaseDeclaration; index: number }, b: { phase: IPhaseDeclaration; index: number }): number =>
        a.phase.order - b.phase.order || a.index - b.index
    )
    .map((entry: { phase: IPhaseDeclaration; index: number }): IPhaseDeclaration => entry.phase);

  // Where layout actually put each phase's steps. A phase with no steps has no opinion and
  // simply takes its share of what is left — that is why it keeps a column at all.
  const leftEdgeByPhase: Map<string, number> = new Map<string, number>();
  for (const step of steps) {
    if (step.phaseId === undefined) continue;
    const current: number | undefined = leftEdgeByPhase.get(step.phaseId);
    if (current === undefined || step.x < current) leftEdgeByPhase.set(step.phaseId, step.x);
  }

  // Boundaries: a column starts halfway between its own leftmost step and the previous
  // phase's leftmost step, so the gap ELK left between two groups of steps becomes the
  // divider. Phases whose steps are missing or out of order fall back to an even share of
  // the remaining width, which keeps the tiling exact in every case.
  const boundaries: number[] = [pool.x];
  for (let i = 1; i < ordered.length; i++) {
    const previousLeft: number | undefined = leftEdgeByPhase.get(ordered[i - 1].id);
    const currentLeft: number | undefined = leftEdgeByPhase.get(ordered[i].id);
    const previousBoundary: number = boundaries[i - 1];
    const remainingColumns: number = ordered.length - i + 1;
    const evenShare: number = previousBoundary + (pool.x + pool.width - previousBoundary) / remainingColumns;

    let boundary: number = evenShare;
    if (previousLeft !== undefined && currentLeft !== undefined && currentLeft > previousLeft) {
      boundary = (previousLeft + currentLeft) / 2;
    } else if (currentLeft !== undefined && currentLeft > previousBoundary + MIN_COLUMN_WIDTH) {
      boundary = currentLeft - COLUMN_PADDING;
    }

    // Keep every column at least MIN_COLUMN_WIDTH wide, and leave room for the ones after it.
    const minBoundary: number = previousBoundary + MIN_COLUMN_WIDTH;
    const maxBoundary: number = pool.x + pool.width - MIN_COLUMN_WIDTH * remainingColumns + MIN_COLUMN_WIDTH;
    boundaries.push(Math.round(Math.min(Math.max(boundary, minBoundary), maxBoundary)));
  }
  boundaries.push(pool.x + pool.width);

  const columns: IPhaseColumnRect[] = ordered.map((phase: IPhaseDeclaration, index: number): IPhaseColumnRect => ({
    phaseId: phase.id,
    x: boundaries[index],
    width: boundaries[index + 1] - boundaries[index]
  }));

  // Anything that contradicts its own declaration gets pulled in — horizontally only, so
  // the lane band a step sits in (its Y) is untouched.
  const columnByPhase: Map<string, IPhaseColumnRect> = new Map<string, IPhaseColumnRect>(
    columns.map((column: IPhaseColumnRect): [string, IPhaseColumnRect] => [column.phaseId, column])
  );
  const nudges: IPhaseStepNudge[] = [];
  for (const step of steps) {
    if (step.phaseId === undefined) continue;
    const column: IPhaseColumnRect | undefined = columnByPhase.get(step.phaseId);
    if (column === undefined) continue;

    const centre: number = step.x + step.width / 2;
    const inside: boolean = centre >= column.x && centre <= column.x + column.width;
    if (inside) continue;

    const minX: number = column.x + COLUMN_PADDING;
    const maxX: number = column.x + column.width - COLUMN_PADDING - step.width;
    // A step wider than its column is centred rather than clamped to a negative range.
    const targetX: number =
      maxX < minX
        ? Math.round(column.x + (column.width - step.width) / 2)
        : Math.round(Math.min(Math.max(step.x, minX), maxX));
    if (targetX !== step.x) nudges.push({ stepId: step.id, x: targetX });
  }

  return { columns, nudges };
}
